/**
 * Pure render-layer constants — no engine imports, so tooling and tests can read these
 * without pulling in Babylon or shader files.
 */

/**
 * Eye height above mean sea level, metres — roughly 30 storeys, with another ~20 storeys
 * of derrick above. Large production platforms genuinely stand this tall from waterline
 * to crown, and the vertical extent is the point: the drop is what makes the water
 * frightening, and structure continuing past the top of the frame is what makes the rig
 * read as a place rather than a prop.
 */
export const DECK_HEIGHT = 105;

/**
 * Where the player stands: forward on a cantilevered observation platform, right at the
 * railing. This matters more than it sounds — from the middle of a deck you cannot see
 * anything below you at all, because the deck you are standing on is in the way. Being at
 * the edge is the only way to look down the legs into the water.
 *
 * 3.2 -> 3.4 on Adam's call, with the rail itself pinned in place (rigBuilder holds it
 * at the old absolute 4.35), so the 20 cm goes into closing the gap to the rail rather
 * than sliding the whole platform north with the eye.
 */
export const EYE_Z = 3.4;

/**
 * 0 -> -2 on Adam's call: two metres to portside. The platform spans x −11..11 and the
 * rail is solid here (its service-catwalk opening is over at 6.9..10.7), so the stance
 * stays on grating with pipe in front of it. The searchlight keeps its absolute mount —
 * the eye stepping west widens their separation to 4.4 m, which only helps the beam
 * read as a beam.
 */
export const EYE_X = -2;

/**
 * The trainable searchlight's mount, just outboard and below the observation platform.
 *
 * Offset from the eye rather than co-located with it: a beam emitted from exactly the
 * viewpoint lights only surfaces already facing the player and produces no visible pool
 * at all, because every lit facet is seen head-on with no shading gradient across it.
 * A few metres of separation is what makes the beam read as a beam.
 */
export const SEARCHLIGHT_MOUNT = { x: 2.4, y: DECK_HEIGHT - 3.5, z: EYE_Z + 1.6 };

/**
 * The strike transient of an arc searchlight, as a 0..1 multiplier on seconds since the
 * switch closed.
 *
 * A carbon or xenon lamp does not simply come on. The arc strikes with a flash brighter
 * than its running output, collapses as the gap ionises, then climbs to full over a few
 * tenths of a second. That stumble is most of what makes a searchlight read as heavy
 * equipment being operated rather than a state being toggled.
 */
export function searchlightStrike(t: number): number {
  if (t >= 0.42) return 1;
  if (t < 0.05) return (t / 0.05) * 1.5;
  if (t < 0.15) return 1.5 - ((t - 0.05) / 0.10) * 1.22;
  return 0.28 + ((t - 0.15) / 0.27) * 0.72;
}

/** Decay after the switch opens: a fast drop with a brief afterglow, spent by 400 ms. */
export function searchlightDecay(t: number): number {
  if (t >= 0.4) return 0;
  const k = 1 - t / 0.4;
  return k * k;
}

/** Vertical field of view of the wide HORIZON view, degrees. */
export const WIDE_FOV_DEG = 60;

/**
 * Scope field of view for a given magnification, derived from the wide view so that
 * "8x" means genuine 8x linear magnification rather than an arbitrary narrow FOV.
 */
export function scopeFovDeg(magnification: number): number {
  const wideHalf = Math.tan((WIDE_FOV_DEG * Math.PI) / 360);
  return (2 * Math.atan(wideHalf / magnification) * 180) / Math.PI;
}

/** Visual horizon radius of the ocean mesh, metres. */
export const OCEAN_RADIUS = 40000;

/**
 * Re-exported from the simulation, which owns it: the safety ring is a rule, not a
 * drawing. Kept available here so engine-free tooling has one constants module to read.
 */
export { SAFETY_ZONE_RADIUS } from '../sim/constants';
